import { routes } from '@/constants/Constants';
import { PermissionName } from '@/utils/types/Permission';
import { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { usePermission } from '@/hooks/usePermission';

type UserPermissionGuardProps = {
  children: ReactNode;
  permission: PermissionName;
  redirectUrl?: string;
  whenNotSet?: 'allow' | 'deny';
};

/**
 * A flexible guard component that checks user permissions and redirects if unauthorized
 * @param props.permission - The permission to check
 * @param props.redirectUrl - The URL to redirect to if unauthorized. Defaults to home route.
 * @param props.whenNotSet - Whether a permission in the `not-set` state allows or denies access. Defaults to 'allow'.
 */
export function UserPermissionGuard(props: UserPermissionGuardProps) {
  const { children, permission, redirectUrl, whenNotSet = 'allow' } = props;

  const { notSet: isPermissionNotSet, enabled: hasPermission } = usePermission(permission);

  const usedRedirectUrl = redirectUrl ?? routes.home;

  if (isPermissionNotSet) {
    if (whenNotSet === 'deny') {
      return <Navigate to={usedRedirectUrl} replace />;
    }
    return <>{children}</>;
  }

  if (!hasPermission) {
    return <Navigate to={usedRedirectUrl} replace />;
  }

  return <>{children}</>;
}
